import { useEffect, useState } from 'react';
import {
  fetchOpsRunbookTemplates,
  type OpsRunbookTemplate,
} from '../../services/opsCopilot';
import {
  incidentsApi,
  type Incident,
  type IncidentActivityItem,
  type IncidentComment,
  type IncidentSafetyEpisode,
} from '../../services/incidentsApi';

type UseIncidentDrawerDataArgs = {
  viewDialogOpen: boolean;
  viewDialogTab: number;
  setViewDialogTab: (tab: number) => void;
  setDrawerAdvancedOpen: (open: boolean) => void;
  selectedIncident: Incident | null;
};

export function useIncidentDrawerData({
  viewDialogOpen,
  viewDialogTab,
  setViewDialogTab,
  setDrawerAdvancedOpen,
  selectedIncident,
}: UseIncidentDrawerDataArgs) {
  const [runbookTemplates, setRunbookTemplates] = useState<OpsRunbookTemplate[]>([]);
  const [incidentActivityItems, setIncidentActivityItems] = useState<IncidentActivityItem[]>([]);
  const [incidentActivityLoading, setIncidentActivityLoading] = useState(false);
  const [incidentActivityError, setIncidentActivityError] = useState<string | null>(null);
  const [incidentComments, setIncidentComments] = useState<IncidentComment[]>([]);
  const [incidentCommentsLoading, setIncidentCommentsLoading] = useState(false);
  const [incidentCommentsError, setIncidentCommentsError] = useState<string | null>(null);
  const [incidentSafetyEpisodes, setIncidentSafetyEpisodes] = useState<IncidentSafetyEpisode[]>([]);
  const [incidentSafetyEpisodesLoading, setIncidentSafetyEpisodesLoading] = useState(false);
  const [incidentSafetyEpisodesError, setIncidentSafetyEpisodesError] = useState<string | null>(null);
  const [incidentSafetyEpisodesAvailable, setIncidentSafetyEpisodesAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (!viewDialogOpen) return;
    fetchOpsRunbookTemplates()
      .then((tpls) => setRunbookTemplates(Array.isArray(tpls) ? tpls : []))
      .catch(() => setRunbookTemplates([]));
  }, [viewDialogOpen]);

  useEffect(() => {
    if (!viewDialogOpen) {
      setDrawerAdvancedOpen(false);
      return;
    }
    if (viewDialogTab > 2) {
      setViewDialogTab(0);
      setDrawerAdvancedOpen(true);
    }
  }, [viewDialogOpen, viewDialogTab, setDrawerAdvancedOpen, setViewDialogTab]);

  useEffect(() => {
    if (!viewDialogOpen || !selectedIncident?.incident_id) {
      setIncidentActivityItems([]);
      setIncidentActivityError(null);
      setIncidentActivityLoading(false);
      return;
    }
    let cancelled = false;
    setIncidentActivityLoading(true);
    setIncidentActivityError(null);
    incidentsApi.listActivity(selectedIncident.incident_id, { limit: 200 })
      .then((res) => {
        if (cancelled) return;
        setIncidentActivityItems(Array.isArray(res?.items) ? res.items : []);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setIncidentActivityItems([]);
        setIncidentActivityError(e?.response?.data?.error || e?.message || 'Failed to load incident activity');
      })
      .finally(() => {
        if (!cancelled) setIncidentActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewDialogOpen, selectedIncident?.incident_id, selectedIncident?.version]);

  useEffect(() => {
    if (!viewDialogOpen || !selectedIncident?.incident_id) {
      setIncidentComments([]);
      setIncidentCommentsError(null);
      setIncidentCommentsLoading(false);
      return;
    }
    let cancelled = false;
    setIncidentCommentsLoading(true);
    setIncidentCommentsError(null);
    incidentsApi.listComments(selectedIncident.incident_id, { limit: 100, offset: 0 })
      .then((res) => {
        if (cancelled) return;
        setIncidentComments(Array.isArray(res?.items) ? res.items : []);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setIncidentComments([]);
        setIncidentCommentsError(e?.response?.data?.error || e?.message || 'Failed to load incident comments');
      })
      .finally(() => {
        if (!cancelled) setIncidentCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewDialogOpen, selectedIncident?.incident_id, selectedIncident?.version]);

  useEffect(() => {
    if (!viewDialogOpen || !selectedIncident?.incident_id) {
      setIncidentSafetyEpisodes([]);
      setIncidentSafetyEpisodesError(null);
      setIncidentSafetyEpisodesLoading(false);
      setIncidentSafetyEpisodesAvailable(null);
      return;
    }
    let cancelled = false;
    setIncidentSafetyEpisodesLoading(true);
    setIncidentSafetyEpisodesError(null);
    incidentsApi.listIncidentSafetyEpisodes(selectedIncident.incident_id)
      .then((res) => {
        if (cancelled) return;
        setIncidentSafetyEpisodes(Array.isArray(res?.episodes) ? res.episodes : []);
        setIncidentSafetyEpisodesAvailable(Boolean(res?.available));
      })
      .catch((e: any) => {
        if (cancelled) return;
        setIncidentSafetyEpisodes([]);
        setIncidentSafetyEpisodesAvailable(false);
        setIncidentSafetyEpisodesError(
          e?.response?.data?.error || e?.message || 'Failed to load incident safety episodes',
        );
      })
      .finally(() => {
        if (!cancelled) setIncidentSafetyEpisodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewDialogOpen, selectedIncident?.incident_id, selectedIncident?.version]);

  return {
    runbookTemplates,
    incidentActivityItems,
    setIncidentActivityItems,
    incidentActivityLoading,
    incidentActivityError,
    incidentComments,
    setIncidentComments,
    incidentCommentsLoading,
    incidentCommentsError,
    setIncidentCommentsError,
    incidentSafetyEpisodes,
    incidentSafetyEpisodesLoading,
    incidentSafetyEpisodesError,
    incidentSafetyEpisodesAvailable,
  };
}
